import { Link } from 'react-router-dom';
import {
  ImageIcon,
  KeyRound,
  DollarSign,
  ShieldAlert,
  Layers,
  Inbox,
  BarChart3,
  ReceiptText,
  Gauge,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { NotificationBadge } from '@/components/ui/notification-badge';
import { usePendingPluginReviewCount } from '../hooks/usePendingPluginReviewCount';
import { useProviderIssueCount } from '../hooks/useProviderIssueCount';
import { analytics } from '@/lib/analytics';

const REVIEW_QUEUE_PATH = '/dev-console/admin/reviews';
const PROVIDERS_PATH = '/dev-console/providers';

export function Dashboard() {
  const pendingReviews = usePendingPluginReviewCount();
  const providerIssues = useProviderIssueCount();
  const tools = [
    {
      name: 'Arche Icon Manager',
      description: 'Upload and manage icons for arches',
      path: '/dev-console/arche-icons',
      icon: ImageIcon,
    },
    {
      name: 'Beta Key Manager',
      description: 'Generate and manage invite keys for closed beta participants',
      path: '/dev-console/beta-keys',
      icon: KeyRound,
    },
    {
      name: 'Payments & Revenue',
      description: 'Platform economics, revenue, costs, refunds, and user management',
      path: '/dev-console/payments',
      icon: DollarSign,
    },
    {
      name: 'Credit Activity',
      description:
        'Platform-wide log of admin credit grants, deductions, and refunds — who, to whom, how much, when',
      path: '/dev-console/credit-activity',
      icon: ReceiptText,
    },
    {
      name: 'Platform Usage',
      description:
        'DAU/MAU, where users spend their time and credits, entity health, daily-burn-cap proximity, per-user activity drill-down',
      path: '/dev-console/analytics',
      icon: BarChart3,
    },
    {
      name: 'Providers',
      description:
        'Upstream provider spend caps & safety — per-provider daily/monthly cap circuit breaker, spend vs cap, and billing-account metadata',
      path: '/dev-console/providers',
      icon: Gauge,
    },
    {
      name: 'Arche Management',
      description: 'Manage featured arches and per-arche suggested arches',
      path: '/dev-console/arche-management',
      icon: Layers,
    },
    {
      name: 'Plugin Review Queue',
      description:
        'Review escalated plugin submissions — approve to release the build, or reject with notes',
      path: '/dev-console/admin/reviews',
      icon: Inbox,
    },
    {
      name: 'Maintenance Mode',
      description: 'Emergency kill switch — take the platform offline for all non-admin users',
      path: '/dev-console/maintenance',
      icon: ShieldAlert,
    },
  ];

  return (
    <div className="max-w-4xl mx-auto p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Dev Console</h1>
        <p className="mt-2 text-muted-foreground">
          Development tools for testing and previewing Fias components
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {tools.map((tool) => {
          const IconComponent = tool.icon;
          return (
            <Link
              key={tool.path}
              to={tool.path}
              onClick={() => analytics.featureUsed('dev_console_tool_open', { tool: tool.name })}
            >
              <Card className="h-full transition-shadow hover:shadow-lg">
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className="text-primary">
                      <IconComponent className="h-8 w-8" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">{tool.name}</CardTitle>
                        {tool.path === REVIEW_QUEUE_PATH && (
                          <NotificationBadge
                            count={pendingReviews}
                            aria-label="Arches awaiting review"
                          />
                        )}
                        {tool.path === PROVIDERS_PATH && (
                          <NotificationBadge
                            count={providerIssues}
                            aria-label="Providers with active issues"
                          />
                        )}
                      </div>
                      <CardDescription className="mt-1">{tool.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default Dashboard;
